import player from "./player";
import piano from "./piano";
import axios from "axios";
import { isEnglishSolfa } from "../common/parameters";

import { pianoHost } from "./paths";
import { IDoPos, IPitch, TClef } from "../common/types";
import { SolfaAudioType } from "./store/user/models";

interface IPlayNote {
    doPos: IDoPos,
    p: IPitch,
    locale: string,
    octave: number,
    isLala: boolean,
    pianoNote: number,
    clef: TClef,
    type: SolfaAudioType,
}

const soundContainer: {[prop: string]: AudioBuffer} = {};
const englishFlatSolfa = [2, 4, 5, 6];
const supportedKeys = [-1, 0, 1, 2, -5, 5, 4, -6, 6, 7, -3, 3, -2, -4, -7];
const shiftedKeys = [-5, 5, 4, -6, 6, 7, -3, -2, -4, -7];
const sourceKey: {[key: number]: IDoPos["i"]} = {
    "-5": 2,
    "5": 0,
    "4": -1,
    "-6": 1,
    "6": 1,
    "7": 2,
    "-3": 2,
    "-2": 3,
    "-4": 3,
    "-7": 0,
};

const octaveShifted = [5];
const upShiftedKeys = [-3, -2];
const min = 53;
const max = 81;

const isShifted = (playNote: IPlayNote) => {
    if (shiftedKeys.indexOf(playNote.doPos.i) !== -1) {
        return true;
    }
    return false;
};
const isUpShifted = (playNote: IPlayNote) => {
    if (upShiftedKeys.indexOf(playNote.doPos.i) !== -1) {
        return true;
    }
    return false;
};



class Voices {
    totalTry = 0;
    sources: AudioBufferSourceNode[] = [];
    gains: AudioParam[] = [];
    getFile = (playNote: IPlayNote) => {
        const {
            doPos,
            p: { s, o, a },
            locale,
            octave,
            isLala,
            pianoNote,
            type,
        } = playNote;
        if (isLala || type === SolfaAudioType.Lala) {
            return `lala/${pianoNote}`;
        }
        let key = doPos.i;
        const isOctaveShifted = octaveShifted.indexOf(key) !== -1;
        if (isShifted(playNote)) {
            key = sourceKey[key];
        }
        const oct = (o || 0) + doPos.o - 4 + octave + (isOctaveShifted ? 1 : 0);
        const isEn = isEnglishSolfa(locale) && a === -1 && englishFlatSolfa.indexOf(s) !== -1;
        const file = `${key}/${s}-${oct}-${a || 0}${isEn ? "en" : ""}`;
        return file;
    };
    load = (playNote: IPlayNote) => {
        let shifted = isShifted(playNote);
        if (
            playNote.pianoNote > max ||
            playNote.pianoNote < min ||
            playNote.locale === "et" ||
            supportedKeys.indexOf(playNote.doPos.i) === -1 ||
            (playNote.pianoNote === max && shifted) ||
            (playNote.clef !== "treble") ||
            playNote.type === "piano"
        ) {
            piano.play(playNote.pianoNote, null, 0.6);
            return false;
        }
        const file = this.getFile(playNote);
        if (soundContainer[file]) return true;
        return new Promise(resolve => {
            axios({
                method: "get",
                url: `${pianoHost}voice/female/${file}.mp3`,
                responseType: "arraybuffer",
            })
                .then(async res => {
                    if (res.data) {
                        await this.makeLoop(res.data, playNote);
                        resolve(true);
                    } else {
                        resolve(false);
                        this.totalTry++;
                        if (this.totalTry < 10) {
                            setTimeout(() => {
                                this.load(playNote);
                            }, 1000);
                        }
                    }
                })
                .catch(err => {
                    resolve(false);
                    this.totalTry++;
                    if (this.totalTry < 10) {
                        setTimeout(() => {
                            this.load(playNote);
                        }, 1000);
                    }
                });
        });
    };
    play = async (playNote: IPlayNote) => {
        const file = this.getFile(playNote);
        const shifted = isShifted(playNote);
        const upShifted = isUpShifted(playNote);
        if (!soundContainer[file]) {
            return this.load(playNote);
        }
        let buffer = soundContainer[file];
        let source = player.context.createBufferSource();;
        let gainNode = player.context.createGain();;
        source.buffer = buffer;
        source.connect(gainNode);
        if (shifted) {
            source.playbackRate.value = 2 ** ((upShifted ? 1 : -1) / 12);
        }
        gainNode.connect(player.context.destination);
        gainNode.gain.value = 0.7;
        let deadline = player.context.currentTime;
        this.sources.push(source);
        source.start(deadline);
        this.gains.push(gainNode.gain);
    };

    makeLoop = (arrayBuffer: ArrayBuffer, playNote: IPlayNote) => {
        return new Promise(resolve => {
            const file = this.getFile(playNote);
            if (soundContainer[file]) {
                resolve(true);
                return;
            }
            player.context.decodeAudioData(arrayBuffer, buffer => {
                soundContainer[file] = buffer;
                resolve(true);
                this.play(playNote);
            });
        });
    };
}

export default new Voices();
